import React, { useEffect, useState } from "react";
import {
  AccordionDetails,
  Grid,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Modal,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  Select,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import { Cross } from "../../../shared/assets/icons";
import CustomInput from "../../../shared/utils/Input";
import moment from "moment";
import Swal from "sweetalert2";
import axios from "axios";
import Decrypt from "../../../shared/utils/Decrypt";
import Spinner from "../../../shared/assets/icons/Spinner.jsx";
import { useParams } from "react-router-dom";

const ModalToUpdateClusters = ({
  openModal,
  cluster,
  closeModal,
  setOpenModalToUpdateCluster,
  onUpdate,
  fetchAllClustersBasedOnCampaignId,
}) => {
  const { id } = useParams();

  // State declarations
  const [jobDetails, setJobDetails] = React.useState({
    paymentAmountPerSqFeet: cluster.paymentAmountPerSqFeet,
    startDate: cluster.jobStartingDate,
    endDate: cluster.jobCompletionDate,
    clusterTitle: cluster.clusterTitle,
  });
  const [openVendorPartnerList, setOpenVendorPartnerList] =
    React.useState(false);
  const [vendorPartnerList, setVendorPartnerList] = React.useState([]);
  const [selectedVendorPartner, setSelectedVendorPartner] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [detailsOfClusterSelected, setDetailsOfClusterSelected] =
    React.useState(cluster);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchVendor, setSearchVendor] = React.useState("");
  const serverURL = process.env.REACT_APP_URL;

  // Handler functions
  const handleClose = () => {
    setOpenModalToUpdateCluster(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setJobDetails((prevData) => {
      const updatedData = { ...prevData, [name]: value };
      return updatedData;
    });
  };

  const handleVPChangeModalOpen = () => {
    setOpenVendorPartnerList(true);
    fetchAllVendorPartners();
  };

  const handleVPChangeModalClose = () => {
    setOpenVendorPartnerList(false);
    setSearchVendor("");
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // API functions
  const fetchAllVendorPartners = async () => {
    try {
      const response = await axios.get(`${serverURL}/api/vendorsForMap`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          adminApproval: 1,
        },
      });
      if (response?.data?.success) {
        setVendorPartnerList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching InstallationPartner:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch vendor partners",
        severity: "error",
      });
    }
  };

  const saveUpdatedCluster = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${process.env.REACT_APP_URL}/vendorClusterBatches/updateVendorClusterBatch/${cluster._id}`,
        {
          jobCompletionDate: jobDetails.endDate,
          jobStartingDate: jobDetails.startDate,
          paymentAmountPerSqFeet: jobDetails.paymentAmountPerSqFeet,
          batches: cluster.batches,
          vendorId: cluster.vendorId?._id,
          clusterTitle: jobDetails?.clusterTitle,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.data?.success) {
        const updatedCluster = {
          ...response.data.data,
          batches: cluster.batches,
          // Ensure we maintain both vendorId and vendorPartnerId references
          vendorId: response.data.data.vendorId,
        };
        fetchAllClustersBasedOnCampaignId(id);

        onUpdate(updatedCluster);
        setOpenModalToUpdateCluster(false);

        Swal.fire({
          customClass: {
            container: "my-swal",
          },
          title: "Success!",
          text: "Cluster updated successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      // Error handling
      console.error("Error while saving created cluster", error);
      // Swal.fire for error on update changes
      Swal.fire({
        customClass: {
          container: "my-swal",
        },
        title: "Error!",
        text: error?.response?.data?.message || "Failed to update cluster.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const saveNewVP = async (vendorPartnerId) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const responseFromSaving = await axios.put(
        `${process.env.REACT_APP_URL}/vendorClusterBatches/updateVendorClusterBatch/${cluster._id}`,
        {
          vendorId: vendorPartnerId,
          batches: cluster.batches,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (responseFromSaving?.data.success) {
        // Create the updated cluster object with the new vendor partner data
        const updatedCluster = {
          ...cluster, // Keep existing cluster data
          ...responseFromSaving.data.data, // Add updated fields from API
          batches: cluster.batches, // Keep existing batches
          vendorPartnerId: vendorPartnerId
            ? vendorPartnerList.find((vp) => vp.id === vendorPartnerId)
            : null,
        };

        // Call onUpdate with the complete updated data
        onUpdate(updatedCluster);

        // Update the local state immediately for UI responsiveness
        setDetailsOfClusterSelected(updatedCluster);

        setOpenVendorPartnerList(false);
        setSelectedVendorPartner(null);
        setSearchVendor("");

        setSnackbar({
          open: true,
          message: vendorPartnerId
            ? "Vendor partner assigned successfully"
            : "Vendor partner unassigned successfully",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error while updating clusters", error);
      setSnackbar({
        open: true,
        message: "Failed to update vendor partner",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <Modal open={openModal} onClose={handleClose}>
        {/* Background Overlay */}
        {/* <div
        className="fixed inset-0 bg-black bg-opacity-50"
        aria-hidden="true"
      /> */}
        <div className="inset-0 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl p-6">
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-4">
              <h2 className="text-lg font-semibold">{`Edit Clusters`}</h2>
              <div onClick={handleClose}>
                <Cross />
              </div>
            </div>

            {/* Batch Details */}
            <div className="my-4">
              <h3 className="text-xl font-bold">{cluster.clusterTitle}</h3>

              {/* Job Timeline & Pay Input */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Grid item xs={12} sm={6} md={3}>
                    <CustomInput
                      name="clusterTitle"
                      placeholder="Enter Cluster Title"
                      title="Cluster Title *"
                      size="small"
                      type="text"
                      onChange={(e) => {
                        handleInputChange(e);
                      }}
                      value={jobDetails?.clusterTitle}
                      required
                      className="border-gray-400 focus:border-gray-300 focus:ring-1 focus:ring-gray-300"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} sx={{ marginTop: "1rem" }}>
                    <CustomInput
                      name="startDate"
                      placeholder="Enter Start Date"
                      title="Starting Date *"
                      size="small"
                      type="date"
                      onChange={(e) => {
                        const selectedDate = moment(
                          e.target.value,
                          "YYYY-MM-DD",
                        );
                        const today = moment().startOf("day");
                        const endDate = moment(
                          jobDetails?.endDate,
                          "YYYY-MM-DD",
                        );

                        // Allow only today or dates greater than today and ensure startDate <= endDate
                        if (
                          selectedDate.isSameOrAfter(today) &&
                          selectedDate.isSameOrBefore(endDate)
                        ) {
                          handleInputChange(e);
                        } else {
                          alert(
                            "Start date cannot be greater than the end date.",
                          );
                        }
                      }}
                      value={moment(jobDetails?.startDate).format("YYYY-MM-DD")}
                      inputProps={{
                        min: moment().format("YYYY-MM-DD"), // Disable dates earlier than today
                        max: moment(jobDetails?.endDate).format("YYYY-MM-DD"), // Disable dates greater than the end date
                      }}
                      required
                      className="border-gray-400 focus:border-gray-300 focus:ring-1 focus:ring-gray-300"
                    />
                  </Grid>
                </div>

                <div>
                  <CustomInput
                    name="paymentAmountPerSqFeet"
                    placeholder="Enter Amount (per sq. feet)"
                    title="Payment (per sq. feet) *"
                    size="small"
                    type="number"
                    value={jobDetails?.paymentAmountPerSqFeet}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value === "" || /^\d+$/.test(value)) {
                        handleInputChange(e);
                      }
                    }}
                    inputProps={{
                      min: "0",
                      onWheel: (e) => {
                        e.target.blur(); // Prevent scroll from changing value
                        e.preventDefault();
                      },
                    }}
                    required
                    className="border-gray-400 focus:border-gray-300 focus:ring-1 focus:ring-gray-300"
                  />
                  <Grid item xs={12} sm={6} md={3}>
                    <Grid item xs={12} sm={6} md={3} sx={{ marginTop: "1rem" }}>
                      <CustomInput
                        name="endDate"
                        placeholder="Enter Last Date"
                        title="Ending Date *"
                        size="small"
                        type="date"
                        onChange={(e) => {
                          const selectedDate = moment(
                            e.target.value,
                            "YYYY-MM-DD",
                          );
                          const startDate = moment(
                            jobDetails?.startDate,
                            "YYYY-MM-DD",
                          );

                          // Allow only dates greater than or equal to the starting date
                          if (selectedDate.isSameOrAfter(startDate)) {
                            handleInputChange(e);
                          } else {
                            alert(
                              "End date cannot be less than the start date.",
                            );
                          }
                        }}
                        value={moment(jobDetails?.endDate).format("YYYY-MM-DD")}
                        inputProps={{
                          min: moment(jobDetails?.startDate).format(
                            "YYYY-MM-DD",
                          ), // Disable dates earlier than the starting date
                        }}
                        required
                        className="border-gray-400 focus:border-gray-300 focus:ring-1 focus:ring-gray-300"
                      />
                    </Grid>
                  </Grid>
                </div>
              </div>

              {/* Assigned IP Section */}
              <div className="bg-gray-100 p-4 rounded-lg mt-6 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <img
                    src={
                      cluster?.vendorId?.profilePhoto ||
                      "/assests/AdminDashboard/userForIP.webp"
                    }
                    alt="IP"
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <p className="text-lg font-semibold">
                      {cluster?.vendorId?.vendorBusinessName ||
                        "No VP Assigned"}
                    </p>
                    <div className="flex items-center text-yellow-500">
                      <span className="ml-1 text-gray-700">
                        {cluster?.vendorPartnerId?.rating}
                      </span>
                    </div>
                    <p className="text-gray-600">
                      {Decrypt(cluster?.vendorPartnerId?.ownerPrimaryContactNo)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      saveNewVP(null);
                    }}
                    className={`text-red-500 border border-red-500 px-3 py-1 rounded-lg ${
                      !cluster?.vendorId ? "cursor-not-allowed opacity-50" : ""
                    }`}
                    disabled={!cluster?.vendorId || isLoading} // Disable if no VP is assigned or if loading
                  >
                    {isLoading ? "Processing..." : "Unassign VP"}
                  </button>
                  <button
                    onClick={handleVPChangeModalOpen}
                    className="text-blue-500 border border-blue-500 px-3 py-1 rounded-lg"
                    disabled={isLoading}
                  >
                    Change VP
                  </button>
                </div>
                <Dialog
                  open={openVendorPartnerList}
                  onClose={handleVPChangeModalClose}
                >
                  <DialogTitle>Select New Vendor Partner</DialogTitle>
                  <DialogContent
                    sx={{
                      height: 350,
                      width: 250,
                      overflow: "auto",
                    }}
                  >
                    <FormControl fullWidth className="custom-dropdown">
                      <input
                        type="search"
                        placeholder="Search Vendor Partner"
                        value={searchVendor}
                        onChange={(e) => setSearchVendor(e.target.value)}
                        className="p-2 text-[14px] border border-spacing-3"
                      />

                      <div className="dropdown-options border-spacing-2 gap-2 mt-2">
                        {vendorPartnerList
                          .filter(
                            (partner) =>
                              partner.vendorBusinessName
                                ?.toLowerCase()
                                .includes(searchVendor.toLowerCase()) ||
                              Decrypt(partner.phoneNumber)
                                ?.toLowerCase()
                                .includes(searchVendor.toLowerCase()),
                          )
                          .map((partner) => (
                            <p
                              className="border border-spacing-1 p-2 text-[14px] cursor-pointer hover:bg-gray-100"
                              key={partner.id}
                              onClick={() => {
                                setSelectedVendorPartner(partner.id);
                                setSearchVendor(partner.vendorBusinessName);
                              }}
                            >
                              {partner.vendorBusinessName} (
                              {Decrypt(partner.phoneNumber) || "No contact"})
                            </p>
                          ))}
                      </div>
                    </FormControl>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleVPChangeModalClose}>Cancel</Button>
                    <Button
                      onClick={async () => {
                        await saveNewVP(selectedVendorPartner);
                        handleVPChangeModalClose();
                      }}
                      color="primary"
                      disabled={!selectedVendorPartner || isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Spinner />
                          Saving...
                        </>
                      ) : (
                        "Save & Exit"
                      )}
                    </Button>
                  </DialogActions>
                </Dialog>
              </div>
              {/* Job Table */}
              {/* <AccordionDetails>
              // Render Job Details Table
              <Box sx={{ overflow: "auto", maxHeight: "16rem" }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: "1rem",
                    boxShadow: "none",
                    maxWidth: "100%",
                    overflowX: "auto",
                  }}
                >
                  <Table sx={{ minWidth: 650, border: "none" }}>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: "#f9f9f9",
                          borderRadius: "1rem",
                        }}
                      >
                        <TableCell sx={tableCellStyle}>City</TableCell>
                        <TableCell sx={tableCellStyle}>Address</TableCell>
                        <TableCell sx={tableCellStyle}>Store Manager</TableCell>
                        <TableCell sx={tableCellStyle}>Job Pay</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cluster?.batch?.jobs?.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell sx={tableCellStyle}>{row.city}</TableCell>
                          <TableCell sx={tableCellStyle}>
                            <LocationAddressComponent
                              storeName={row.storeName}
                              address={row.address}
                              city={row.city}
                              state={row.state}
                              zipCode={row.zipCode}
                            />
                          </TableCell>
                          <TableCell sx={tableCellStyle}>
                            <StoreManagerDetailsComponent
                              storeOwnerName={row.storeOwnerName}
                              storeOwnerPhoneNumber={row.storeOwnerPhoneNumber}
                            />
                          </TableCell>
                          <TableCell sx={tableCellStyle}>
                            ₹{row.paymentAmountPerSqFeet}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </AccordionDetails> */}

              {/* Update Button */}
              <div className="my-4 flex justify-end">
                <button
                  className="bg-blue-600 text-white px-5 py-2 rounded-lg disabled:bg-blue-300"
                  disabled={
                    !jobDetails.paymentAmountPerSqFeet ||
                    !jobDetails.startDate ||
                    !jobDetails.endDate ||
                    isLoading
                  }
                  onClick={saveUpdatedCluster}
                  // setOpenConfirmationModal(true);
                >
                  {isLoading ? "Updating..." : "Update Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000} // Changed from 6000 to 5000
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ModalToUpdateClusters;
